<!-- 登录弹窗 -->
<script lang="ts" setup>
import { googleLogin, microsoftLogin, microsoftCallback } from '~/apis/login'
import { useAuthStore } from '~/stores/authStore'
import { useToastHandler } from '~/composables/useToastHandler'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const loginStore = useLoginStore()
const myProfileStore = useMyProfileStore()
const { showErrorToast, showSuccessToast } = useToastHandler()

const googleLoginLoading = ref(false)
const microsoftLoginLoading = ref(false)
let googleScriptPromise: Promise<void> | null = null
const hasHandledMicrosoftCallback = ref(false)

const showPassword = ref(false)
// 切换密码可见性
const togglePasswordVisibility = () => {
  showPassword.value = !showPassword.value
}

const showRegisterPassword = ref(false)
// 切换密码可见性
const toggleRegisterPasswordVisibility = () => {
  showRegisterPassword.value = !showRegisterPassword.value
}

const showRegisterConfirmPassword = ref(false)
// 切换密码可见性
const toggleRegisterConfirmPasswordVisibility = () => {
  showRegisterConfirmPassword.value = !showRegisterConfirmPassword.value
}

const handleBackClick = () => {
  const isForgetFlow = loginStore.pageType === 'ForgetPasswordSignUpVerifyCode' || loginStore.pageType === 'ForgetPasswordSignUpRegister'
  if (isForgetFlow && loginStore.fromMyProfile) {
    loginStore.closeLoginModal()
    myProfileStore.openMyProfileModal()
    return
  }
  loginStore.togglePageType('SignIn')
}

// 登录后如有需要重定向
const redirectAfterLoginIfNeeded = () => {
  if (!import.meta.client) return
  const redirectPath = sessionStorage.getItem('redirectAfterLogin')
  if (redirectPath && redirectPath !== '/') {
    sessionStorage.removeItem('redirectAfterLogin')
    router.push(redirectPath)
  }
}

const trackGtagConversion = (sendTo: string, params?: Record<string, any>) => {
  if (!import.meta.client) return
  const gtag = (window as any)?.gtag
  if (typeof gtag !== 'function') return
  gtag('event', 'conversion', {
    send_to: sendTo,
    ...(params || {})
  })
}

const trackRegistrationConversionOnce = (user: any, source: 'google' | 'microsoft') => {
  if (!import.meta.client) return

  const explicitIsNew = user?.is_new_user ?? user?.isNewUser ?? user?.new_user ?? user?.is_new ?? user?.isNew ?? user?.first_login ?? user?.firstLogin ?? user?.is_register ?? user?.isRegister
  const isExplicitlyNew = explicitIsNew === 1 || explicitIsNew === '1' || explicitIsNew === true
  if (!isExplicitlyNew) return

  const idPart = user?.id != null
    ? `id:${String(user.id)}`
    : (user?.email ? `email:${String(user.email).toLowerCase()}` : 'unknown')
  const storageKey = `gtag_complete_registration_${idPart}`
  try {
    if (localStorage.getItem(storageKey) === '1') return
  } catch {
    // ignore
  }

  trackGtagConversion('AW-17924652563/L7aECMqsl_EbEJP8keNC')

  try {
    localStorage.setItem(storageKey, '1')
  } catch {
    // ignore
  }
}

// #region Google 登录
// 确保 Google 脚本已加载
const ensureGoogleScriptLoaded = async () => {
  if (!import.meta.client) {
    showErrorToast('Google login is only available in the browser.')
  }

  if ((window as any)?.google?.accounts?.id) {
    return
  }

  if (!googleScriptPromise) {
    googleScriptPromise = new Promise<void>((resolve, reject) => {
      const existingScript = document.querySelector<HTMLScriptElement>('script[data-google-identity="true"]')
      if (existingScript) {
        // 检查脚本是否已经加载完成
        if ((window as any)?.google?.accounts?.id) {
          resolve()
          return
        }
        existingScript.addEventListener('load', () => {
          // 延迟检查，确保 SDK 完全初始化
          setTimeout(() => {
            if ((window as any)?.google?.accounts?.id) {
              resolve()
            } else {
              reject(new Error('Google SDK loaded but not available.'))
            }
          }, 100)
        }, { once: true })
        existingScript.addEventListener('error', () => {
          reject(new Error('Failed to load Google login script. Please check your network connection.'))
        }, { once: true })
        return
      }

      const script = document.createElement('script')
      script.src = 'https://accounts.google.com/gsi/client'
      script.async = true
      script.defer = true
      script.dataset.googleIdentity = 'true'
      script.onload = () => {
        // 延迟检查，确保 SDK 完全初始化
        setTimeout(() => {
          if ((window as any)?.google?.accounts?.id) {
            resolve()
          } else {
            reject(new Error('Google SDK loaded but not available.'))
          }
        }, 100)
      }
      script.onerror = () => {
        reject(new Error('Failed to load Google login script. Please check your network connection and try again.'))
      }
      document.head.appendChild(script)

      // 设置超时
      setTimeout(() => {
        if (!(window as any)?.google?.accounts?.id) {
          reject(new Error('Google login script loading timeout. Please try again.'))
        }
      }, 10000) // 10 秒超时
    })
  }

  await googleScriptPromise
}

// 获取 Google idToken（依赖 Google Identity Services SDK）
const getGoogleIdToken = async (): Promise<string> => {
  if (!import.meta.client) {
    showErrorToast('Google login is only available in the browser.')
  }

  await ensureGoogleScriptLoaded()

  const google = (window as any)?.google
  if (!google?.accounts?.id) {
    showErrorToast('Google login SDK is not available.')
  }

  const config = useRuntimeConfig()
  const clientId = (config.public as any)?.googleClientId as string | undefined
  if (!clientId) {
    showErrorToast('Google client id is not configured.')
  }

  return await new Promise<string>((resolve, reject) => {
    let callbackExecuted = false
    let timeoutId: ReturnType<typeof setTimeout> | null = null
    let buttonContainer: HTMLDivElement | null = null

    // 清理函数
    const cleanup = () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
        timeoutId = null
      }
      if (buttonContainer && document.body.contains(buttonContainer)) {
        setTimeout(() => {
          if (buttonContainer && document.body.contains(buttonContainer)) {
            document.body.removeChild(buttonContainer)
          }
        }, 1000)
      }
    }

    try {
      // 初始化 Google Identity Services
      google.accounts.id.initialize({
        client_id: clientId,
        callback: (response: any) => {
          callbackExecuted = true
          cleanup()
          if (response?.credential) {
            resolve(response.credential as string)
          } else {
            reject(new Error('Failed to get Google credential.'))
          }
        },
        auto_select: false,
        cancel_on_tap_outside: true
      })

      // 创建一个临时的容器来渲染按钮
      buttonContainer = document.createElement('div')
      buttonContainer.id = 'google-login-temp-container'
      buttonContainer.style.position = 'fixed'
      buttonContainer.style.left = '-9999px'
      buttonContainer.style.top = '-9999px'
      buttonContainer.style.opacity = '0'
      buttonContainer.style.pointerEvents = 'none'
      buttonContainer.style.width = '200px'
      buttonContainer.style.height = '40px'
      document.body.appendChild(buttonContainer)

      // 渲染 Google 登录按钮
      google.accounts.id.renderButton(buttonContainer, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: 'signin_with',
        shape: 'rectangular',
        width: 200
      })

      // 等待按钮渲染完成，然后触发点击
      const checkAndClick = (attempts = 0) => {
        if (attempts > 20) {
          // 最多尝试 20 次（2 秒）
          cleanup()
          reject(new Error('Failed to render Google login button. Please try again.'))
          return
        }

        const button = buttonContainer?.querySelector('div[role="button"]') as HTMLElement
        if (button) {
          // 触发按钮点击事件
          button.click()

          // 设置超时，如果 30 秒内没有响应，认为失败
          timeoutId = setTimeout(() => {
            if (!callbackExecuted) {
              cleanup()
              reject(new Error('Google login timeout. Please try again.'))
            }
          }, 30000)
        } else {
          // 如果按钮还没有渲染完成，等待一下再试
          setTimeout(() => checkAndClick(attempts + 1), 100)
        }
      }

      // 延迟检查，确保按钮已渲染
      setTimeout(() => checkAndClick(), 200)
    } catch (error) {
      cleanup()
      reject(error instanceof Error ? error : new Error('Google login failed.'))
    }
  })
}

const handleGoogleLoginClick = async () => {
  if (googleLoginLoading.value) return
  googleLoginLoading.value = true
  try {
    const idToken = await getGoogleIdToken()
    const response = await googleLogin({
      keyword: '',
      invitedBy: '',
      idToken
    })

    if (!response.success || !response.data?.token) {
      showErrorToast(response.message || 'Google login failed.')
    }

    await authStore.setSession({
      token: response.data.token,
      user: ({
        ...(response.data.user || {}),
        downgrade: (response.data.user as any)?.downgrade ?? ''
      } as any)
    })

    trackRegistrationConversionOnce(response.data.user, 'google')
    showSuccessToast('Login successful!')
    loginStore.closeLoginModal()
    redirectAfterLoginIfNeeded()
  } catch (error: any) {
    const message = error?.message || 'Google login failed.'
    showErrorToast(message)
  } finally {
    googleLoginLoading.value = false
  }
}
// #endregion

// #region Microsoft 登录
const handleMicrosoftLoginClick = async () => {
  if (microsoftLoginLoading.value) return
  microsoftLoginLoading.value = true
  try {
    const response = await microsoftLogin()
    if (!response.success || !response.data?.authUrl) {
      showErrorToast(response.message || 'Microsoft login URL not found.')
    }

    const authUrl = response.data.authUrl

    // 生成并保存 state 参数（用于 CSRF 防护）
    let state: string
    if (import.meta.client && typeof crypto !== 'undefined' && crypto.randomUUID) {
      state = crypto.randomUUID()
    } else {
      // 降级方案：使用时间戳 + 随机数
      state = `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`
    }

    // 保存 state 到 sessionStorage
    if (import.meta.client) {
      sessionStorage.setItem('microsoft_oauth_state', state)
    }

    // 将 state 添加到 URL（如果 URL 中还没有 state 参数）
    try {
      const urlObj = new URL(authUrl)
      if (!urlObj.searchParams.has('state')) {
        urlObj.searchParams.set('state', state)
        if (import.meta.client) {
          window.location.href = urlObj.toString()
        }
      } else {
        // 如果 URL 中已经有 state，使用现有的
        if (import.meta.client) {
          window.location.href = authUrl
        }
      }
    } catch {
      // 如果 URL 解析失败，直接使用原始 URL
      if (import.meta.client) {
        window.location.href = authUrl
      }
    }
  } catch (error: any) {
    const message = error?.message || 'Microsoft login failed.'
    showErrorToast(message)
    // 清除保存的 state
    if (import.meta.client) {
      sessionStorage.removeItem('microsoft_oauth_state')
    }
  } finally {
    // 注意：这里不重置 loading 状态，因为页面会跳转
    // 但如果跳转失败，loading 状态会在错误处理中保持
  }
}

const clearMicrosoftQueryParams = () => {
  if (!import.meta.client) return
  const query = { ...route.query }
  if (!query.code && !query.state) return
  delete query.code
  delete query.state
  router.replace({ query })
}

// 处理 Microsoft 回调
const handleMicrosoftCallbackIfPresent = async () => {
  if (!import.meta.client || hasHandledMicrosoftCallback.value) return
  const { code, state } = route.query
  if (!code || !state) return

  // 确保登录弹窗打开
  if (!loginStore.isOpenLoginModal) {
    loginStore.openLoginModal('SignIn')
  }

  // 验证 state 参数（CSRF 防护）
  const savedState = sessionStorage.getItem('microsoft_oauth_state')
  if (savedState && String(state) !== savedState) {
    // showErrorToast('Invalid state parameter. Possible security issue. Please try again.')
    clearMicrosoftQueryParams()
    if (savedState) {
      sessionStorage.removeItem('microsoft_oauth_state')
    }
    return
  }

  hasHandledMicrosoftCallback.value = true
  microsoftLoginLoading.value = true

  try {
    const response = await microsoftCallback({
      code: String(code),
      state: String(state),
      keyword: '',
      invitedBy: ''
    })

    if (!response.success || !response.data?.token) {
      showErrorToast(response.message || 'Microsoft login failed.')
    }

    await authStore.setSession({
      token: response.data.token,
      user: {
        ...(response.data.user || {}),
        downgrade: (response.data.user as any)?.downgrade ?? ''
      }
    })

    trackRegistrationConversionOnce(response.data.user, 'microsoft')
    showSuccessToast('Login successful!')
    loginStore.closeLoginModal()
    redirectAfterLoginIfNeeded()
  } catch (error: any) {
    const message = error?.message || 'Microsoft login failed.'
    showErrorToast(message)
  } finally {
    microsoftLoginLoading.value = false
    // 清除保存的 state
    if (import.meta.client) {
      sessionStorage.removeItem('microsoft_oauth_state')
    }
    clearMicrosoftQueryParams()
  }
}

// 监听路由变化，处理 Microsoft 回调
watch(() => route.query, () => {
  if (import.meta.client) {
    handleMicrosoftCallbackIfPresent()
  }
}, {
  immediate: true
})

onMounted(() => {
  handleMicrosoftCallbackIfPresent()
})
// #endregion

// #region 服务条款、隐私政策、退款政策
// Terms of Service 预览（静态 HTML，放置于 public，支持直接 iframe 内嵌）
const termsHtmlUrl = '/legal/terms-of-service.html'
const isTermsModalOpen = ref(false)
const openTermsOfService = () => {
  isTermsModalOpen.value = true
}
const closeTermsOfService = () => {
  isTermsModalOpen.value = false
}

// Privacy Policy 预览（静态 HTML，放置于 public，支持直接 iframe 内嵌）
const privacyHtmlUrl = '/legal/privacy-policy.html'
const isPrivacyModalOpen = ref(false)
const openPrivacyPolicy = () => {
  isPrivacyModalOpen.value = true
}
const closePrivacyPolicy = () => {
  isPrivacyModalOpen.value = false
}

// Refund Policy 预览（静态 HTML，放置于 public，支持直接 iframe 内嵌）
const refundHtmlUrl = '/legal/refund-policy.html'
const isRefundModalOpen = ref(false)
const openRefundPolicy = () => {
  isRefundModalOpen.value = true
}
const closeRefundPolicy = () => {
  isRefundModalOpen.value = false
}
// #endregion

// 仅在客户端渲染含 iframe 的弹窗，避免 SSR Hydration mismatch
const isClient = ref(false)
onMounted(() => {
  isClient.value = true
})
</script>

<template>
  <UModal
    v-model:open="loginStore.isOpenLoginModal"
    :ui="{
      overlay: 'bg-[#101012B2] backdrop-blur-[3px]',
      content: 'w-[calc(100%-32px)] md:w-[376px] bg-[#2D2D39] rounded-[16px] ring-[none]'
    }"
  >
    <template #content>
      <!-- 内层背景内容 -->
      <div class="relative w-full h-full font-['Roboto'] py-[32px] px-[24px]">
        <!-- 关闭按钮 -->
        <div
          class="absolute top-[10px] right-[10px] flex items-center justify-center w-[34px] h-[34px] cursor-pointer before:content-[''] before:absolute before:w-[20px] before:h-[2px] before:bg-[#8888A1] before:rotate-45 before:rounded-[20px] after:content-[''] after:absolute after:w-[20px] after:h-[2px] after:bg-[#8888A1] after:-rotate-45 after:rounded-[20px]"
          @click="loginStore.closeLoginModal()"
        />

        <!-- 标题 -->
        <h2 class="text-[22px] text-[#E7E7EC] font-bold text-center leading-[32px] mb-[24px]">
          {{ loginStore.pageType === 'SignIn' ? 'Sign in' : loginStore.pageType === 'SignUpVerifyCode' || loginStore.pageType === 'SignUpRegister' ? 'Sign Up' : 'Recover your password' }}
        </h2>

        <!-- 社交登录 -->
        <div
          v-if="loginStore.pageType !== 'ForgetPasswordSignUpVerifyCode' && loginStore.pageType !== 'ForgetPasswordSignUpRegister'"
          class="flex justify-center gap-[32px]"
        >
          <!-- Google 登录 -->
          <UButton
            type="button"
            variant="ghost"
            color="neutral"
            class="flex items-center h-[44px] px-[42px] bg-[#383847] hover:bg-[#383847] active:bg-[#383847] cursor-pointer flex items-center justify-center transition-opacity"
            :loading="googleLoginLoading"
            :disabled="googleLoginLoading || microsoftLoginLoading"
            :ui="{ leadingIcon: 'text-[#5E5E77]' }"
            @click="handleGoogleLoginClick"
          >
            <img
              class="w-[16px] h-[16px]"
              src="../assets/icons/google.png"
              alt="Sign in with Google"
            >
          </UButton>

          <!-- Microsoft 登录 -->
          <UButton
            type="button"
            variant="ghost"
            color="neutral"
            class="flex items-center h-[44px] px-[42px] bg-[#383847] hover:bg-[#383847] active:bg-[#383847] cursor-pointer flex items-center justify-center transition-opacity"
            :loading="microsoftLoginLoading"
            :disabled="googleLoginLoading || microsoftLoginLoading"
            :ui="{ leadingIcon: 'text-[#5E5E77]' }"
            @click="handleMicrosoftLoginClick"
          >
            <img
              class="w-[16px] h-[16px]"
              src="../assets/icons/microsoft.png"
              alt="Sign in with Microsoft"
            >
          </UButton>
        </div>

        <!-- 分隔线 -->
        <div
          v-if="loginStore.pageType !== 'ForgetPasswordSignUpVerifyCode' && loginStore.pageType !== 'ForgetPasswordSignUpRegister'"
          class="flex items-center gap-[16px] mt-[16px] mb-[24px]"
        >
          <div class="flex-1 h-[1px] bg-[#5E5E77]" />
          <span class="text-[14px] text-[#8888A1] leading-[20px]">or</span>
          <div class="flex-1 h-[1px] bg-[#5E5E77]" />
        </div>

        <!-- 登录表单内容 -->
        <UForm
          v-if="loginStore.pageType === 'SignIn'"
          class="flex flex-col gap-[16px]"
          :schema="loginStore.loginSchema"
          :state="loginStore"
          :validate-on="[]"
          @submit="loginStore.handleLogin()"
        >
          <!-- 登录弹窗 Email 输入框 -->
          <UFormField
            label="Email address"
            name="signInEmail"
            :ui="{
              label: 'w-full text-[14px] text-[#E7E7EC] leading-[20px] mb-[4px]',
              container: 'mt-0',
              error: `font-['Roboto'] text-[12px] text-[#FF383C] leading-[16px] mt-[4px]`
            }"
          >
            <UInput
              v-model="loginStore.signInEmail"
              class="w-full"
              type="text"
              placeholder="example@email.com"
              :ui="{
                base: 'w-full h-[40px] text-[16px] md:text-[14px] text-[#E7E7EC] leading-[20px] bg-[#1E1E2C] rounded-[4px] border-[1px] border-transparent py-[10px] px-[12px] ring-[none] placeholder:text-[#5E5E77] focus-visible:!border-[1px] focus-visible:!border-solid focus-visible:!border-[#E7E7EC] focus-visible:!ring-[none]'
              }"
            />
          </UFormField>

          <!-- 登录弹窗 Password 输入框 -->
          <UFormField
            label="Password"
            name="password"
            :ui="{
              label: 'w-full text-[14px] text-[#E7E7EC] leading-[20px] mb-[4px]',
              container: 'mt-0',
              error: `font-['Roboto'] text-[12px] text-[#FF383C] leading-[16px] mt-[4px]`
            }"
          >
            <template #label="{ label }">
              <div class="flex items-center justify-between w-full">
                <span>{{ label }}</span>
                <span
                  class="text-[12px] text-[#8888A1] cursor-pointer"
                  @click="loginStore.togglePageType('ForgetPasswordSignUpVerifyCode')"
                >
                  Forget Password
                </span>
              </div>
            </template>
            <UInput
              v-model="loginStore.password"
              class="w-full"
              :type="showPassword ? 'text' : 'password'"
              placeholder="Enter password"
              :ui="{
                base: 'w-full h-[40px] text-[16px] md:text-[14px] text-[#E7E7EC] leading-[20px] bg-[#1E1E2C] rounded-[4px] border-[1px] border-transparent py-[10px] px-[12px] ring-[none] placeholder:text-[#5E5E77] focus-visible:!border-[1px] focus-visible:!border-solid focus-visible:!border-[#E7E7EC] focus-visible:!ring-[none]'
              }"
            >
              <template #trailing>
                <UIcon
                  :name="showPassword ? 'i-lucide-eye-off' : 'i-lucide-eye'"
                  class="w-[16px] h-[16px] text-[#8888A1] cursor-pointer"
                  @click="togglePasswordVisibility"
                />
              </template>
            </UInput>
          </UFormField>

          <!-- 登录弹窗 Login 按钮 -->
          <UButton
            class="flex justify-center w-full h-[40px] bg-[#BF2AFF] hover:bg-[#590080] active:bg-[#590080] disabled:bg-[#590080] rounded-[8px] border-none mt-[8px] cursor-pointer"
            :label="loginStore.loginLoading ? '' : 'Login' "
            type="submit"
            :loading="loginStore.loginLoading"
            :ui="{
              label: 'text-[14px] text-white text-center'
            }"
          />
        </UForm>

        <!-- 注册-验证邮箱验证码 表单内容 -->
        <UForm
          v-if="loginStore.pageType === 'SignUpVerifyCode' || loginStore.pageType === 'ForgetPasswordSignUpVerifyCode'"
          class="flex flex-col gap-[16px]"
          :schema="loginStore.codeVerifySchema"
          :state="loginStore"
          :validate-on="[]"
          @submit="loginStore.handleCodeVerify()"
        >
          <!-- 注册弹窗 Email 输入框 -->
          <UFormField
            label="Email address"
            name="signUpEmail"
            :error="loginStore.signUpEmailError || undefined"
            :ui="{
              label: 'w-full text-[14px] text-[#E7E7EC] leading-[20px] mb-[4px]',
              container: 'mt-0',
              error: `font-['Roboto'] text-[12px] text-[#FF383C] leading-[16px] mt-[4px]`
            }"
          >
            <UInput
              v-model="loginStore.signUpEmail"
              class="w-full"
              type="text"
              placeholder="example@email.com"
              :ui="{
                base: 'w-full h-[40px] text-[16px] md:text-[14px] text-[#E7E7EC] leading-[20px] bg-[#1E1E2C] rounded-[4px] border-[1px] border-transparent py-[10px] px-[12px] ring-[none] placeholder:text-[#5E5E77] focus-visible:!border-[1px] focus-visible:!border-solid focus-visible:!border-[#E7E7EC] focus-visible:!ring-[none]'
              }"
            />
          </UFormField>

          <!-- 注册弹窗 邮箱验证码输入框 -->
          <UFormField
            label="Email Verification Code"
            name="emailVerificationCode"
            :error="loginStore.emailVerificationCodeError || undefined"
            :ui="{
              label: 'w-full text-[14px] text-[#E7E7EC] leading-[20px] mb-[4px]',
              container: 'mt-0',
              error: `font-['Roboto'] text-[12px] text-[#FF383C] leading-[16px] mt-[4px]`
            }"
          >
            <UInput
              v-model="loginStore.emailVerificationCode"
              class="w-full"
              type="text"
              placeholder="Check your email for the code"
              :ui="{
                base: 'w-full h-[40px] text-[16px] md:text-[14px] text-[#E7E7EC] leading-[20px] bg-[#1E1E2C] rounded-[4px] border-[1px] border-transparent py-[10px] px-[12px] ring-[none] placeholder:text-[#5E5E77] focus-visible:!border-[1px] focus-visible:!border-solid focus-visible:!border-[#E7E7EC] focus-visible:!ring-[none]',
                trailing: 'pe-[4px]'
              }"
              @input="loginStore.emailVerificationCodeError = ''"
            >
              <template #trailing>
                <UButton
                  :class="`flex justify-center w-[108px] h-[32px] bg-[#383847] border-[1px] border-solid border-[#383847] hover:border-[#454564] hover:bg-transparent active:bg-transparent disabled:bg-transparent disabled:opacity-100 ${loginStore.countdown > 0 ? 'disabled:border-transparent' : 'disabled:border-[#454564]'} rounded-[8px] cursor-pointer`"
                  :label="loginStore.codeSending ? 'sending' : loginStore.countdown > 0 ? `Resend in (${loginStore.countdown})s` : loginStore.hasSentCode ? 'Resend Code' : 'Send Code'"
                  type="button"
                  :loading="loginStore.codeSending"
                  :disabled="loginStore.countdown > 0 || loginStore.codeSending"
                  :ui="{
                    label: `text-[12px] ${loginStore.countdown > 0 ? 'text-[#B8B8C7]' : 'text-white'} text-center leading-[14px]`,
                    leadingIcon: 'w-[18px] h-[18px]'
                  }"
                  @click="loginStore.handleSendCode()"
                />
              </template>
            </UInput>
          </UFormField>

          <!-- 注册弹窗 Continue 按钮 -->
          <UButton
            v-if="loginStore.pageType === 'SignUpVerifyCode'"
            class="flex justify-center w-full h-[40px] bg-[#BF2AFF] hover:bg-[#590080] active:bg-[#590080] disabled:bg-[#590080] rounded-[8px] border-none mt-[8px] cursor-pointer"
            :label="loginStore.codeVerifying ? '' : 'Continue' "
            type="submit"
            :loading="loginStore.codeVerifying"
            :ui="{
              label: 'text-[14px] text-white text-center'
            }"
          />

          <div
            v-else
            class="flex justify-between gap-[24px]"
          >
            <UButton
              class="flex justify-center w-full h-[40px] bg-transparent hover:bg-[#383847] active:bg-[#383847] rounded-[8px] border-[1px] border-solid border-[#454564] mt-[8px] cursor-pointer"
              label="Back"
              icon="i-lucide-arrow-left"
              type="button"
              :ui="{
                label: 'text-[14px] text-white text-center',
                leadingIcon: 'w-[16px] h-[16px] text-[#E7E7EC]'
              }"
              @click="handleBackClick"
            />
            <UButton
              class="flex justify-center w-full h-[40px] bg-[#BF2AFF] hover:bg-[#590080] active:bg-[#590080] disabled:bg-[#590080] rounded-[8px] border-none mt-[8px] cursor-pointer"
              :label="loginStore.codeVerifying ? '' : 'Next' "
              icon="i-lucide-arrow-right"
              type="submit"
              :loading="loginStore.codeVerifying"
              :ui="{
                label: 'text-[14px] text-white text-center',
                leadingIcon: 'w-[16px] h-[16px] text-[#E7E7EC]'
              }"
            />
          </div>
        </UForm>

        <!-- 注册-确认密码 表单内容  -->
        <UForm
          v-if="loginStore.pageType === 'SignUpRegister' || loginStore.pageType === 'ForgetPasswordSignUpRegister'"
          class="flex flex-col gap-[16px]"
          :schema="loginStore.registerSchema"
          :state="loginStore"
          :validate-on="[]"
          @submit="loginStore.handleRegister()"
        >
          <!-- 注册弹窗 password 输入框 -->
          <UFormField
            label="Password"
            name="registerPassword"
            :ui="{
              label: 'w-full text-[14px] text-[#E7E7EC] leading-[20px] mb-[4px]',
              container: 'mt-0',
              error: `font-['Roboto'] text-[12px] text-[#FF383C] leading-[16px] mt-[4px]`
            }"
          >
            <UInput
              v-model="loginStore.registerPassword"
              class="w-full"
              :type="showRegisterPassword ? 'text' : 'password'"
              placeholder="Enter password"
              :ui="{
                base: 'w-full h-[40px] text-[16px] md:text-[14px] text-[#E7E7EC] leading-[20px] bg-[#1E1E2C] rounded-[4px] border-[1px] border-transparent py-[10px] px-[12px] ring-[none] placeholder:text-[#5E5E77] focus-visible:!border-[1px] focus-visible:!border-solid focus-visible:!border-[#E7E7EC] focus-visible:!ring-[none]'
              }"
              @focus="loginStore.onRegisterPasswordFocus"
              @click="loginStore.onRegisterPasswordFocus"
              @blur="loginStore.onRegisterPasswordBlur"
            >
              <template #trailing>
                <UIcon
                  :name="showRegisterPassword ? 'i-lucide-eye-off' : 'i-lucide-eye'"
                  class="w-[16px] h-[16px] text-[#8888A1] cursor-pointer"
                  @click="toggleRegisterPasswordVisibility"
                />
              </template>
            </UInput>
          </UFormField>
          <ul
            v-if="loginStore.showPasswordRequirements"
            class="space-y-1 mt-[-12px]"
            aria-label="Password requirements"
          >
            <li
              v-for="(req, index) in loginStore.strength"
              :key="index"
              class="flex items-center gap-[4px]"
              :class="req.met ? 'text-[#27C840]' : 'text-[#E7E7EC]'"
            >
              <UIcon
                :name="req.met ? 'mingcute:check-fill' : 'mingcute:close-fill'"
                class="size-[16px]"
              />
              <span class="text-[12px] leading-[16px]">
                {{ req.text }}
              </span>
            </li>
          </ul>

          <!-- 注册弹窗 Confirm Password 输入框 -->
          <UFormField
            label="Confirm Password"
            name="registerConfirmPassword"
            :error="loginStore.registerConfirmPasswordError"
            :ui="{
              label: 'w-full text-[14px] text-[#E7E7EC] leading-[20px] mb-[4px]',
              container: 'mt-0',
              error: `font-['Roboto'] text-[12px] text-[#FF383C] leading-[16px] mt-[4px]`
            }"
          >
            <UInput
              v-model="loginStore.registerConfirmPassword"
              class="w-full"
              :type="showRegisterConfirmPassword ? 'text' : 'password'"
              placeholder="Re-enter password"
              :ui="{
                base: 'w-full h-[40px] text-[16px] md:text-[14px] text-[#E7E7EC] leading-[20px] bg-[#1E1E2C] rounded-[4px] border-[1px] border-transparent py-[10px] px-[12px] ring-[none] placeholder:text-[#5E5E77] focus-visible:!border-[1px] focus-visible:!border-solid focus-visible:!border-[#E7E7EC] focus-visible:!ring-[none]'
              }"
              @blur="loginStore.onRegisterConfirmPasswordBlur"
            >
              <template #trailing>
                <UIcon
                  :name="showRegisterConfirmPassword ? 'i-lucide-eye-off' : 'i-lucide-eye'"
                  class="w-[16px] h-[16px] text-[#8888A1] cursor-pointer"
                  @click="toggleRegisterConfirmPasswordVisibility"
                />
              </template>
            </UInput>
          </UFormField>

          <!-- 注册弹窗 Confirm 按钮 -->
          <UButton
            class="flex justify-center w-full h-[40px] bg-[#BF2AFF] hover:bg-[#590080] active:bg-[#590080] disabled:bg-[#590080] rounded-[8px] border-none mt-[8px] cursor-pointer"
            :label="loginStore.registerLoading ? '' : 'Confirm' "
            type="submit"
            :loading="loginStore.registerLoading"
            :ui="{
              label: 'text-[14px] text-white text-center'
            }"
          />
        </UForm>

        <!-- 法律文本 -->
        <p
          v-if="loginStore.pageType === 'SignIn'"
          class="text-[12px] text-[#8888A1] text-center leading-[16px] mt-[24px]"
        >
          By proceeding, you acknowledge that you (i) agree to the
          <button
            type="button"
            class="underline text-white cursor-pointer inline p-0 bg-transparent border-none"
            @click="openTermsOfService"
          >
            Terms of Service
          </button>
          <span class="underline text-white">,</span>
          and (ii) have read and understand our
          <button
            type="button"
            class="underline text-white cursor-pointer inline p-0 bg-transparent border-none"
            @click="openPrivacyPolicy"
          >
            Privacy Policy
          </button>
          and
          <button
            type="button"
            class="underline text-white cursor-pointer inline p-0 bg-transparent border-none"
            @click="openRefundPolicy"
          >
            Refund Policy
          </button>.
        </p>

        <!-- 注册链接 -->
        <p
          v-if="loginStore.pageType !== 'ForgetPasswordSignUpVerifyCode' && loginStore.pageType !== 'ForgetPasswordSignUpRegister'"
          class="text-[14px] text-[#FFFFFF] text-center leading-[20px] mt-[24px]"
        >
          {{ loginStore.pageType === 'SignIn' ? "Don't have an account yet ?" : "Already have an account ?" }}
          <span
            class="text-[#D880FF] pl-[8px] cursor-pointer"
            @click="loginStore.togglePageType(loginStore.pageType === 'SignIn' ? 'SignUpVerifyCode' : 'SignIn')"
          >
            {{ loginStore.pageType === 'SignIn' ? 'Sign up Now' : 'Sign in Now' }}
          </span>
        </p>
      </div>
    </template>
  </UModal>

  <ClientOnly v-if="isClient">
    <!-- Terms of Service 内嵌预览弹窗 -->
    <UModal
      v-model:open="isTermsModalOpen"
      :ui="{
        overlay: 'bg-[#101012B2] backdrop-blur-[3px]',
        content: 'w-[calc(100%-32px)] md:max-w-[960px] bg-[#2D2D39] rounded-[16px] ring-none'
      }"
    >
      <template #content>
        <div class="flex justify-between items-center px-[24px] pt-[20px] pb-[12px] border-b border-[#1E1E2C]">
          <div class="text-[18px] text-white font-medium">
            Terms of Service
          </div>
          <button
            type="button"
            class="text-[#8888A1] hover:text-white transition-colors"
            @click="closeTermsOfService"
          >
            <UIcon
              name="mingcute:close-fill"
              class="w-[20px] h-[20px]"
            />
          </button>
        </div>
        <div class="h-[70vh] min-h-[480px] bg-[#0D0D14]">
          <iframe
            :src="termsHtmlUrl"
            title="Terms of Service"
            class="w-full h-full border-none"
            sandbox="allow-same-origin allow-forms allow-scripts allow-popups allow-popups-to-escape-sandbox"
            referrerpolicy="no-referrer"
          />
        </div>
      </template>
    </UModal>

    <!-- Privacy Policy 内嵌预览弹窗 -->
    <UModal
      v-model:open="isPrivacyModalOpen"
      :ui="{
        overlay: 'bg-[#101012B2] backdrop-blur-[3px]',
        content: 'w-[calc(100%-32px)] md:max-w-[960px] bg-[#2D2D39] rounded-[16px] ring-none'
      }"
    >
      <template #content>
        <div class="flex justify-between items-center px-[24px] pt-[20px] pb-[12px] border-b border-[#1E1E2C]">
          <div class="text-[18px] text-white font-medium">
            Privacy Policy
          </div>
          <button
            type="button"
            class="text-[#8888A1] hover:text-white transition-colors"
            @click="closePrivacyPolicy"
          >
            <UIcon
              name="mingcute:close-fill"
              class="w-[20px] h-[20px]"
            />
          </button>
        </div>
        <div class="h-[70vh] min-h-[480px] bg-[#0D0D14]">
          <iframe
            :src="privacyHtmlUrl"
            title="Privacy Policy"
            class="w-full h-full border-none"
            sandbox="allow-same-origin allow-forms allow-scripts allow-popups allow-popups-to-escape-sandbox"
            referrerpolicy="no-referrer"
          />
        </div>
      </template>
    </UModal>

    <!-- Refund Policy 内嵌预览弹窗 -->
    <UModal
      v-model:open="isRefundModalOpen"
      :ui="{
        overlay: 'bg-[#101012B2] backdrop-blur-[3px]',
        content: 'w-[calc(100%-32px)] md:max-w-[960px] bg-[#2D2D39] rounded-[16px] ring-none'
      }"
    >
      <template #content>
        <div class="flex justify-between items-center px-[24px] pt-[20px] pb-[12px] border-b border-[#1E1E2C]">
          <div class="text-[18px] text-white font-medium">
            Refund Policy
          </div>
          <button
            type="button"
            class="text-[#8888A1] hover:text-white transition-colors"
            @click="closeRefundPolicy"
          >
            <UIcon
              name="mingcute:close-fill"
              class="w-[20px] h-[20px]"
            />
          </button>
        </div>
        <div class="h-[70vh] min-h-[480px] bg-[#0D0D14]">
          <iframe
            :src="refundHtmlUrl"
            title="Refund Policy"
            class="w-full h-full border-none"
            sandbox="allow-same-origin allow-forms allow-scripts allow-popups allow-popups-to-escape-sandbox"
            referrerpolicy="no-referrer"
          />
        </div>
      </template>
    </UModal>
  </ClientOnly>
</template>
